<template>
  <v-dialog
    :value="!!alerta"
    max-width="440"
    scrollable
    transition="dialog-bottom-transition"
    @input="(v) => !v && dispensar()"
  >
    <v-card v-if="alerta" class="ca-card">
      <header class="ca-header" :class="{ 'ca-header--inicio': alerta.motivo === 'inicio' }">
        <span class="ca-pulse">
          <v-icon color="white">{{ alerta.motivo === "inicio" ? "mdi-bell-ring" : "mdi-account-arrow-right" }}</v-icon>
        </span>
        <div class="ca-header__text">
          <span class="ca-title">{{ titulo }}</span>
          <span class="ca-subtitle">{{ subtitulo }}</span>
        </div>
      </header>

      <!-- Corpo num cartão; no aviso de início o filete fica laranja, como o cabeçalho. -->
      <v-card-text class="ds-dialog-body">
        <section
          class="ds-form-section ca-body"
          :class="{ 'ds-form-section--warning': alerta.motivo === 'inicio' }"
        >
          <v-avatar size="64" class="ca-avatar">
            <v-img :src="foto" />
          </v-avatar>

          <div class="ca-info">
            <p v-if="alerta.patientName" class="ca-name">{{ alerta.patientName }}</p>
            <p v-else-if="alerta.texto" class="ca-texto">{{ alerta.texto }}</p>

            <div class="ca-chips">
              <span v-if="horario" class="ca-chip">
                <v-icon x-small>mdi-clock-outline</v-icon>
                {{ horario }}
              </span>
              <span v-if="relativo" class="ca-chip" :class="`ca-chip--${relativo.tom}`">
                {{ relativo.texto }}
              </span>
              <span v-if="alerta.emEspera" class="ca-chip ca-chip--espera">
                <v-icon x-small>mdi-seat-outline</v-icon>
                já está em espera
              </span>
            </div>
          </div>
        </section>
      </v-card-text>

      <v-card-actions class="ca-actions ds-dialog-actions">
        <v-btn outlined color="primary" @click="dispensar">Agora não</v-btn>
        <v-spacer />
        <v-btn depressed class="ca-iniciar" @click="iniciar">
          <v-icon left small>mdi-play-circle-outline</v-icon>
          Iniciar atendimento
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { formatTime } from "@/utils/datetime";
import { getProfileImage } from "@/utils/getProfileImage";

const AVATAR_PADRAO = require("@/assets/images/profile_default_image.png");

/**
 * salvia-779 — popup global "inicie a próxima consulta".
 *
 * Mora no App.vue, fora de qualquer tela: o aviso de início pode chegar com o
 * médico na agenda, na lista de pacientes ou no prontuário de outro paciente.
 * O conteúdo vem do módulo `atendimento` do Vuex, que decide QUANDO mostrar.
 *
 * "Iniciar" usa a mesma porta do detalhe do compromisso e do dock
 * (salvia-837/778): `?atendimento=novo` no prontuário. A mudança da consulta
 * para "Em Atendimento" no kanban é feita lá, pelo próprio atendimento — aqui
 * só se navega, para haver um lugar só que mexe no status.
 */
export default {
  name: "ConsultaAlertaDialog",

  data() {
    return {
      foto: AVATAR_PADRAO,
      agora: Date.now(),
      relogio: null,
      piscaTitulo: null,
      tituloOriginal: null,
    };
  },

  computed: {
    alerta() {
      return this.$store.state.atendimento.alerta;
    },
    titulo() {
      return this.alerta?.motivo === "inicio"
        ? "Consulta começando"
        : "Próxima consulta";
    },
    subtitulo() {
      return this.alerta?.motivo === "inicio"
        ? "Seu próximo paciente está chegando"
        : "Deseja já iniciar o próximo atendimento?";
    },
    horario() {
      return this.alerta?.start ? `às ${formatTime(this.alerta.start)}` : null;
    },
    /** "em 4 min" / "agora" / "atrasada 12 min" — atualiza a cada 30s. */
    relativo() {
      if (!this.alerta?.start) return null;
      const diffMin = Math.round((new Date(this.alerta.start) - this.agora) / 60000);
      if (!Number.isFinite(diffMin)) return null;
      if (diffMin > 0) return { texto: `em ${diffMin} min`, tom: "futuro" };
      if (diffMin === 0) return { texto: "agora", tom: "agora" };
      return { texto: `atrasada ${-diffMin} min`, tom: "atraso" };
    },
  },

  watch: {
    alerta: {
      immediate: true,
      async handler(alerta) {
        clearInterval(this.relogio);
        this.pararDeChamarAtencao();
        this.foto = AVATAR_PADRAO;
        if (!alerta) return;

        this.chamarAtencaoSeEmSegundoPlano(alerta);
        this.agora = Date.now();
        this.relogio = setInterval(() => (this.agora = Date.now()), 30000);

        if (alerta.patientPhoto) {
          try {
            this.foto = await getProfileImage(alerta.patientPhoto);
          } catch {
            this.foto = AVATAR_PADRAO;
          }
        }
      },
    },
  },

  mounted() {
    document.addEventListener("visibilitychange", this.aoVoltarParaAba);
  },

  beforeDestroy() {
    clearInterval(this.relogio);
    this.pararDeChamarAtencao();
    document.removeEventListener("visibilitychange", this.aoVoltarParaAba);
  },

  methods: {
    /**
     * Com o Salvia em outra aba, o popup abre sem ninguém ver — e o aviso de
     * "consulta começando" perde a razão de existir. O título pisca até o
     * médico voltar; se o navegador JÁ deu permissão de notificação, vai
     * também uma do sistema. Não pedimos permissão aqui: pedido que surge do
     * nada, fora de um clique, os navegadores bloqueiam ou o usuário recusa.
     */
    chamarAtencaoSeEmSegundoPlano(alerta) {
      if (!document.hidden) return;

      this.tituloOriginal = document.title;
      const aviso = alerta.motivo === "inicio" ? "🔔 Consulta começando" : "🔔 Próxima consulta";
      let alterna = false;
      this.piscaTitulo = setInterval(() => {
        alterna = !alterna;
        document.title = alterna ? aviso : this.tituloOriginal;
      }, 1000);

      try {
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          const nativa = new Notification(this.titulo, {
            body: alerta.texto || alerta.patientName || "",
            tag: `salvia-consulta-${alerta.eventId ?? ""}`,
          });
          nativa.onclick = () => {
            window.focus();
            nativa.close();
          };
        }
      } catch {
        // Notificação do sistema é bônus; o popup continua lá.
      }
    },

    pararDeChamarAtencao() {
      if (!this.piscaTitulo) return;
      clearInterval(this.piscaTitulo);
      this.piscaTitulo = null;
      if (this.tituloOriginal) document.title = this.tituloOriginal;
    },

    aoVoltarParaAba() {
      if (!document.hidden) this.pararDeChamarAtencao();
    },

    marcarNotificacaoLida() {
      const id = this.alerta?.notificationId;
      if (id) this.$store.dispatch("notifications/markAsRead", id).catch(() => {});
    },

    dispensar() {
      // O aviso já foi visto: não fica pendente no sino.
      this.marcarNotificacaoLida();
      this.$store.commit("atendimento/LIMPAR_ALERTA");
    },

    iniciar() {
      const patientId = this.alerta?.patientId;
      const eventId = this.alerta?.eventId;
      this.marcarNotificacaoLida();
      this.$store.commit("atendimento/LIMPAR_ALERTA");
      if (!patientId) return;

      // `id` como string: é a chave do <router-view> (ver useNotificationTypes).
      this.$router
        .push({
          path: `/patient-profile-dashboard/${String(patientId)}`,
          // `event`: liga o atendimento a esta consulta (histórico do kanban).
          query: { atendimento: "novo", ...(eventId ? { event: String(eventId) } : {}) },
        })
        .catch(() => {});
    },
  },
};
</script>

<style scoped>
.ca-card {
  border-radius: 16px !important;
  overflow: hidden;
  background: var(--ds-surface-white, #fff);
}

.ca-header {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 14px;
  padding: 18px 20px;
  color: #fff;
  background:
    radial-gradient(circle at 92% -20%, rgba(255, 255, 255, 0.22) 0%, transparent 45%),
    linear-gradient(135deg, var(--ds-primary) 0%, var(--ds-primary-deep) 100%);
}
.ca-header--inicio {
  background:
    radial-gradient(circle at 92% -20%, rgba(255, 255, 255, 0.22) 0%, transparent 45%),
    linear-gradient(135deg, #c2410c 0%, var(--ds-cat-amber) 100%);
}
.ca-header__text {
  display: flex;
  flex-direction: column;
}
.ca-title {
  font-size: 1.1rem;
  font-weight: 700;
  line-height: 1.2;
}
.ca-subtitle {
  font-size: 0.8rem;
  opacity: 0.88;
}

/* Anel pulsando: chama o olho sem precisar de som. */
.ca-pulse {
  position: relative;
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
}
.ca-pulse::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 50%;
  box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.55);
  animation: ca-pulse 1.8s ease-out infinite;
}
@keyframes ca-pulse {
  to {
    box-shadow: 0 0 0 14px rgba(255, 255, 255, 0);
  }
}

.ca-body {
  display: flex;
  align-items: center;
  gap: 16px;
}
.ca-avatar {
  flex-shrink: 0;
  box-shadow: 0 0 0 3px var(--ds-surface-white, #fff), 0 0 0 5px var(--ds-primary-border);
}
.ca-info {
  min-width: 0;
}
.ca-name {
  margin: 0 0 6px;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--ds-text, #2c3e50);
}
.ca-texto {
  margin: 0 0 6px;
  font-size: 0.92rem;
  color: var(--ds-text, #2c3e50);
}
.ca-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.ca-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 600;
  color: var(--ds-primary-darker);
  background: var(--ds-primary-surface);
}
.ca-chip .v-icon {
  color: inherit;
}
.ca-chip--agora,
.ca-chip--atraso {
  color: #9a3412;
  background: #ffedd5;
}
.ca-chip--espera {
  color: #92400e;
  background: #fef3c7;
}

.ca-iniciar {
  color: #fff !important;
  border-radius: var(--ds-radius-btn, 8px);
  background: linear-gradient(135deg, #c2410c 0%, var(--ds-cat-amber) 100%) !important;
  box-shadow: 0 4px 12px rgba(180, 83, 9, 0.3);
}

@media (prefers-reduced-motion: reduce) {
  .ca-pulse::after {
    animation: none;
  }
}
</style>
